import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Checkbox } from "./checkbox";
import type {
	ColumnDef,
	FilterDef,
	QuickFilterDef,
	RowSelectionOptions,
} from "./types";
import type { FwQueryFilter, FwQueryState } from "./useFwQuery";

export function useDataTable<T>(props: UseDataTableProps<T>) {
	// State for search and filters
	//const [searchTerm, setSearchTerm] = useState("");
	const [activeQuickFilter, setActiveQuickFilter] = useState<string>("");

	// State for sorting
	const [sortField, setSortField] = useState<string>("");
	const [sortOrder, setSortOrder] = useState<SortDirection>("asc");

	// State for row selection
	const [selectedRows, setSelectedRows] = useState<T[]>([]);

	// State to store signatures of quick filters (what FwQueryFilter[] they apply)
	const [quickFilterSignatures, setQuickFilterSignatures] = useState<
		Record<string, FwQueryFilter<T>[] | null>
	>({});
	const quickFilterActionInProgress = useRef(false); // Ref to prevent re-entrant calls

	// Effect to generate signatures for quick filters
	useEffect(() => {
		if (!props.quickFilters) {
			setQuickFilterSignatures({});
			return;
		}
		const signatures: Record<string, FwQueryFilter<T>[] | null> = {};
		props.quickFilters.forEach((qf) => {
			// Mock FwQueryState to capture what setFiltersAsync would be called with
			const mockQuery = {
				setFiltersAsync: (filtersToSet: FwQueryFilter<T>[]) => {
					signatures[qf.id] = filtersToSet;
				},
				// Use fixed, default values for mock query state to ensure stable signatures
				filters: [] as FwQueryFilter<T>[], // Signature should represent application from a baseline
				pageNo: 1,
				pageSize: 20, // A common default page size
				sort: "",

				loadAsync: async () => {
					console.warn(
						"mockQuery.loadAsync called during signature generation for",
						qf.id,
					);
				},
				reset: () => {
					console.warn(
						"mockQuery.reset called during signature generation for",
						qf.id,
					);
				},
				firstPageAsync: async () => {
					console.warn(
						"mockQuery.firstPageAsync called during signature generation for",
						qf.id,
					);
				},
				prevPageAsync: async () => {
					console.warn(
						"mockQuery.prevPageAsync called during signature generation for",
						qf.id,
					);
				},
				nextPageAsync: async () => {
					console.warn(
						"mockQuery.nextPageAsync called during signature generation for",
						qf.id,
					);
				},
				lastPageAsync: async () => {
					console.warn(
						"mockQuery.lastPageAsync called during signature generation for",
						qf.id,
					);
				},
				setPageNoAsync: async (_newPageNo: number) => {
					console.warn(
						"mockQuery.setPageNoAsync called during signature generation for",
						qf.id,
						_newPageNo,
					);
				},
				setPageSizeAsync: async (_newPageSize: number) => {
					console.warn(
						"mockQuery.setPageSizeAsync called during signature generation for",
						qf.id,
						_newPageSize,
					);
				},
				setSortAsync: async (_newSort: string) => {
					console.warn(
						"mockQuery.setSortAsync called during signature generation for",
						qf.id,
						_newSort,
					);
				},
				// Provide all other properties from FwQueryState with default/dummy values
				isPending: false,
				errorMessage: "",
				items: [],
				totalItems: 0,
				totalPages: 0,
				url: "",
				firstBtnDisabled: true,
				prevBtnDisabled: true,
				nextBtnDisabled: true,
				lastBtnDisabled: true,
				pageNoBtnDisabled: true,
				pageSizeBtnDisabled: true,
			};
			try {
				qf.filterFn(mockQuery as unknown as FwQueryState<T>); // Cast to any for the mock
				if (typeof signatures[qf.id] === "undefined") {
					// If filterFn didn't call setFiltersAsync, its signature is considered null
					signatures[qf.id] = null;
				}
			} catch (e) {
				console.error(
					`Error generating signature for quick filter ${qf.id}:`,
					e,
				);
				signatures[qf.id] = null;
			}
		});
		// Only update if signatures actually changed to avoid potential loops if deep comparison is not perfect
		if (JSON.stringify(quickFilterSignatures) !== JSON.stringify(signatures)) {
			setQuickFilterSignatures(signatures);
		}
	}, [props.quickFilters, quickFilterSignatures]); // Signature generation should only depend on quickFilters definition

	// Effect to synchronize DataTable's internal sort state with query.sort from useFwQuery
	useEffect(() => {
		const currentSortString = props.query.sort;
		if (currentSortString) {
			const parts = currentSortString.split(" ");
			if (parts.length === 2) {
				const newSortField = parts[0];
				const newSortOrder = parts[1].toLowerCase() as "asc" | "desc";

				if (newSortOrder === "asc" || newSortOrder === "desc") {
					if (sortField !== newSortField || sortOrder !== newSortOrder) {
						setSortField(newSortField);
						setSortOrder(newSortOrder);
					}
				} else {
					// Invalid sort order, clear local sort state
					if (sortField !== "" || sortOrder !== "asc") {
						setSortField("");
						setSortOrder("asc");
					}
				}
			} else {
				// Invalid format, clear local sort state
				if (sortField !== "" || sortOrder !== "asc") {
					setSortField("");
					setSortOrder("asc");
				}
			}
		} else {
			// query.sort is empty, clear local sort state
			if (sortField !== "" || sortOrder !== "asc") {
				setSortField("");
				setSortOrder("asc");
			}
		}
	}, [props.query.sort, sortField, sortOrder]); // include sortField and sortOrder to avoid stale closures but rely on inner checks

	useEffect(() => {
		// This effect synchronizes the active quick filter button based on query.filters
		// It should run when query.filters changes or when quickFilterSignatures are updated.

		let newActiveQuickFilterId = "";

		if (props.query.filters && props.query.filters.length > 0) {
			const currentFiltersString = JSON.stringify(
				props.query.filters
					.slice()
					.sort((a, b) => String(a.field).localeCompare(String(b.field))),
			);
			for (const qfId in quickFilterSignatures) {
				const signatureFilters = quickFilterSignatures[qfId];
				if (signatureFilters) {
					// Ensure signature filters are also sorted for consistent comparison
					const signatureFiltersString = JSON.stringify(
						signatureFilters
							.slice()
							.sort((a, b) => String(a.field).localeCompare(String(b.field))),
					);
					if (signatureFiltersString === currentFiltersString) {
						newActiveQuickFilterId = qfId;
						break;
					}
				}
			}
			// if (newActiveQuickFilterId) {
			//   // console.log(`DataTable: Matched query.filters to quick filter '${newActiveQuickFilterId}'.`);
			// } else {
			//   console.log(`DataTable: Sync Effect - query.filters present but no matching signature. query.filters:`, JSON.stringify(query.filters), `Signatures:`, JSON.stringify(quickFilterSignatures));
			// }
		} else {
			// query.filters is empty or null
			for (const qfId in quickFilterSignatures) {
				const signatureFilters = quickFilterSignatures[qfId];
				if (signatureFilters && signatureFilters.length === 0) {
					newActiveQuickFilterId = qfId;
					break;
				}
			}
			// if (newActiveQuickFilterId) {
			//   // console.log(`DataTable: query.filters is empty. Matched to quick filter '${newActiveQuickFilterId}' (likely 'All').`);
			// } else if (quickFilters && quickFilters.length > 0 && !activeQuickFilter) {
			//    // This handles the very initial load case where URL is empty, query.filters is empty,
			//    // and no quick filter button is active yet. We want to set the "All" (empty signature) filter active.
			//    // We already tried to find an empty signature above. If not found, this path might be redundant
			//    // or could pick the first if no true "empty signature" filter exists.
			//    // For safety, let's assume the loop above should have found the "All" filter if it exists and has an empty signature.
			//    // If newActiveQuickFilterId is still "", it means no defined QF results in empty filters.
			//    // console.log("DataTable: query.filters empty, no empty signature QF found, and no QF active. No default set by this path.");
			// }
		}

		if (activeQuickFilter !== newActiveQuickFilterId) {
			//console.log(`DataTable: Sync Effect - Changing activeQuickFilter from '${activeQuickFilter}' to '${newActiveQuickFilterId}'. Query Filters:`, JSON.stringify(query.filters));
			setActiveQuickFilter(newActiveQuickFilterId);
		}
	}, [props.query.filters, quickFilterSignatures, activeQuickFilter]);

	// Function to apply quick filter
	const handleQuickFilterChange = useCallback(
		async (filterId: string) => {
			// activeQuickFilter below will be from the closure when this useCallback instance was created.
			// This is usually what we want for the guard to prevent re-processing the same ID if already active.
			if (
				/* filterId !== activeQuickFilter && */ !quickFilterActionInProgress.current
			) {
				// Temporarily remove the activeQuickFilter check from guard to see if it's involved in a loop
				// The primary guard against re-entrancy is now quickFilterActionInProgress.
				// We still want to apply the filter even if the button was already active (e.g. user re-clicks "All")

				quickFilterActionInProgress.current = true;
				//console.log(`DataTable: handleQuickFilterChange - Setting activeQuickFilter to '${filterId}'`);
				setActiveQuickFilter(filterId); // Optimistically set button state

				const selectedFilter = props.quickFilters?.find(
					(f) => f.id === filterId,
				);
				try {
					if (selectedFilter) {
						//console.log(`DataTable: handleQuickFilterChange - Applying filterFn for '${filterId}'`);
						await selectedFilter.filterFn(props.query);
					} else {
						//console.warn(`DataTable: handleQuickFilterChange - No selectedFilter found for ID '${filterId}'. Applying empty filters.`);
						await props.query.setFiltersAsync([]);
					}
				} finally {
					quickFilterActionInProgress.current = false;
				}
			}
			// else if (quickFilterActionInProgress.current) {
			//   console.warn(`DataTable: Quick filter change for '${filterId}' ignored, action already in progress.`);
			// } else if (filterId === activeQuickFilter) {
			//   console.log(`DataTable: Quick filter '${filterId}' re-clicked, but no action taken as it is already active and not in progress.`);
			//   // Optionally, you could force a reload if re-clicking an active filter should refresh data:
			//   // quickFilterActionInProgress.current = true;
			//   // await query.loadAsync();
			//   // quickFilterActionInProgress.current = false;
			// }
		},
		[props.quickFilters, props.query],
	); // Removed activeQuickFilter from here

	// Function to handle sorting
	const handleSort = useCallback(
		async (columnId: string) => {
			// Find the column to get the correct field name
			const column = props.columns.find((col) => col.id === columnId);
			if (!column) {
				//console.error(`Column with id ${columnId} not found`);
				return;
			}

			// Use accessorKey as the sort field if available, otherwise use the column id
			const sortFieldName = column.accessorKey?.toString() || columnId;

			//console.log(`⭐ Sort header clicked: Column=${columnId}`);
			//console.log(`Current state - Field: ${sortField}, Order: ${sortOrder}`);

			try {
				//let newSortField = columnId;
				let newSortOrder: "asc" | "desc" = "asc";

				// Toggle order if the same column is clicked
				if (sortField === columnId) {
					// Flip the sort order
					newSortOrder = sortOrder === "asc" ? "desc" : "asc";
					//console.log(`⮎ Same column clicked - changing order to ${newSortOrder}`);
				} else {
					// New column selected - default to ascending
					//console.log(`⮎ New column selected - setting order to asc`);
				}

				// Create the sort string that will be used for the API request
				const sortString = `${sortFieldName} ${newSortOrder}`;
				//console.log(`🔄 Final sort string: "${sortString}"`);

				// Update UI state FIRST to keep the UI responsive
				setSortField(columnId);
				setSortOrder(newSortOrder);

				// Then perform the API request with our sort string
				await props.query.setSortAsync(sortString);

				//console.log('✅ Sort operation completed');
			} catch (error) {
				console.error("❌ Error during sorting:", error);
			}
		},
		[props.query, sortField, sortOrder, props.columns],
	);

	// Helper function to get row ID
	const getRowId = useCallback(
		(item: T): string => {
			if (!props.rowSelection) return "";

			// Handle ID field function or key
			if (typeof props.rowSelection.idField === "function") {
				return props.rowSelection.idField(item);
			} else if (props.rowSelection.idField) {
				return String(item[props.rowSelection.idField]);
			}

			// Default to index in query.items
			return String(props.query.items.indexOf(item));
		},
		[props.rowSelection, props.query.items],
	);

	// Function to check if a row is selected
	const isRowSelected = useCallback(
		(item: T): boolean => {
			if (!props.rowSelection?.enabled) return false;

			const itemId = getRowId(item);
			return selectedRows.some((row) => getRowId(row) === itemId);
		},
		[props.rowSelection, selectedRows, getRowId],
	);

	// Function to toggle row selection
	const toggleRowSelection = useCallback(
		(item: T, event?: React.MouseEvent): void => {
			if (!props.rowSelection?.enabled) return;

			// Stop event propagation if it exists
			if (event) {
				event.stopPropagation();
			}

			const itemId = getRowId(item);

			if (props.rowSelection?.multiple) {
				// Toggle multiple selection
				setSelectedRows((prev) => {
					const isSelected = prev.some((row) => getRowId(row) === itemId);

					if (isSelected) {
						// Remove from selection
						const newSelection = prev.filter((row) => getRowId(row) !== itemId);

						// Notify about change
						if (props.rowSelection?.onSelectionChange) {
							props.rowSelection.onSelectionChange(newSelection);
						}

						return newSelection;
					} else {
						// Add to selection
						const newSelection = [...prev, item];

						// Notify about change
						if (props.rowSelection?.onSelectionChange) {
							props.rowSelection.onSelectionChange(newSelection);
						}

						return newSelection;
					}
				});
			} else {
				// Single selection
				setSelectedRows((prev) => {
					const isSelected = prev.length === 1 && getRowId(prev[0]) === itemId;

					if (isSelected) {
						// Deselect
						if (props.rowSelection?.onSelectionChange) {
							props.rowSelection.onSelectionChange([]);
						}
						return [];
					} else {
						// Select only this row
						if (props.rowSelection?.onSelectionChange) {
							props.rowSelection.onSelectionChange([item]);
						}
						return [item];
					}
				});
			}
		},
		[props.rowSelection, getRowId],
	);

	// Function to handle select all rows
	const toggleSelectAll = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			if (!props.rowSelection?.enabled || !props.rowSelection?.multiple) return;

			if (event.target.checked) {
				// Select all
				setSelectedRows(props.query.items);
				if (props.rowSelection?.onSelectionChange) {
					props.rowSelection?.onSelectionChange(props.query.items);
				}
			} else {
				// Deselect all
				setSelectedRows([]);
				if (props.rowSelection?.onSelectionChange) {
					props.rowSelection?.onSelectionChange([]);
				}
			}
		},
		[props.rowSelection, props.query.items],
	);

	// Prep columns with selection checkbox if needed
	const effectiveColumns = useMemo(() => {
		if (!props.rowSelection?.enabled) return props.columns;

		// Create selection column
		const renderSelectionCell = (item: T) => (
			<Checkbox
				checked={isRowSelected(item)}
				onCheckedChange={() => toggleRowSelection(item)}
				onClick={(e) => e.stopPropagation()}
			/>
		);

		const selectionColumn: ColumnDef<T> = {
			id: "selection",
			header: props.rowSelection?.multiple
				? // For multiple selection, include a header checkbox for select all
					() => (
						<Checkbox
							checked={
								props.query.items.length > 0 &&
								selectedRows.length === props.query.items.length
							}
							indeterminate={
								selectedRows.length > 0 &&
								selectedRows.length < props.query.items.length
							}
							onCheckedChange={(checked) =>
								toggleSelectAll({
									target: { checked: !!checked },
								} as React.ChangeEvent<HTMLInputElement>)
							}
						/>
					)
				: // For single selection, just leave header empty
					"",
			sortable: false,
			className: "w-[50px]",
			cell: renderSelectionCell,
			mobile: {
				hideLabel: true,
				order: -1,
				wrapperClassName: "flex-row items-center justify-start",
				cell: renderSelectionCell,
			},
		};

		// Add selection column to beginning of columns
		return [selectionColumn, ...props.columns];
	}, [
		props.columns,
		props.rowSelection,
		isRowSelected,
		toggleRowSelection,
		selectedRows,
		props.query.items,
		toggleSelectAll,
	]);

	return new DataTableState<T>({
		activeQuickFilter,
		setActiveQuickFilter,
		sortField,
		setSortField,
		sortOrder,
		setSortOrder,
		selectedRows,
		setSelectedRows,
		quickFilterSignatures,
		setQuickFilterSignatures,
		quickFilterActionInProgress,
		handleQuickFilterChange,
		handleSort,
		getRowId,
		isRowSelected,
		toggleRowSelection,
		toggleSelectAll,
		effectiveColumns,
		query: props.query,
		columns: props.columns,
		quickFilters: props.quickFilters,
		filters: props.filters,
		onRowClick: props.onRowClick,
		rowClassName: props.rowClassName,
		emptyMessage: props.emptyMessage,
		title: props.title,
		description: props.description,
		showQuickFilters: props.showQuickFilters,
		rowSelection: props.rowSelection,
		showPager: props.showPager,
	});
}

export type SortDirection = "asc" | "desc";
export type QuickFilterSignature<T> = Record<string, FwQueryFilter<T>[] | null>;

export class DataTableState<T> {
	/** Get the selected quick filter. */
	activeQuickFilter: string;

	/** Set the selected quick filter. */
	setActiveQuickFilter: React.Dispatch<React.SetStateAction<string>>;

	sortField: string;
	setSortField: React.Dispatch<React.SetStateAction<string>>;

	sortOrder: SortDirection;
	setSortOrder: React.Dispatch<React.SetStateAction<SortDirection>>;

	selectedRows: T[];
	setSelectedRows: React.Dispatch<React.SetStateAction<T[]>>;

	quickFilterSignatures: QuickFilterSignature<T>;
	setQuickFilterSignatures: React.Dispatch<QuickFilterSignature<T>>;

	quickFilterActionInProgress: React.RefObject<boolean>;
	handleQuickFilterChange: (filterId: string) => Promise<void>;
	handleSort: (columnId: string) => Promise<void>;
	getRowId: (item: T) => string;
	isRowSelected: (item: T) => boolean;
	toggleRowSelection: (
		item: T,
		event?: React.MouseEvent<Element, MouseEvent> | undefined,
	) => void;
	toggleSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;

	effectiveColumns: ColumnDef<T>[];
	query: FwQueryState<T>;
	columns: ColumnDef<T>[];
	quickFilters?: QuickFilterDef<T>[];
	filters?: FilterDef<T>[];
	onRowClick?: (item: T) => void;
	rowClassName?: (item: T) => string;
	emptyMessage?: string;
	title?: string;
	description?: string;
	showQuickFilters?: boolean;
	rowSelection?: RowSelectionOptions<T>;
	showPager?: boolean;

	constructor(props: {
		activeQuickFilter: string;
		setActiveQuickFilter: React.Dispatch<React.SetStateAction<string>>;
		sortField: string;
		setSortField: React.Dispatch<React.SetStateAction<string>>;
		sortOrder: SortDirection;
		setSortOrder: React.Dispatch<React.SetStateAction<SortDirection>>;
		selectedRows: T[];
		setSelectedRows: React.Dispatch<React.SetStateAction<T[]>>;
		quickFilterSignatures: QuickFilterSignature<T>;
		setQuickFilterSignatures: React.Dispatch<QuickFilterSignature<T>>;
		quickFilterActionInProgress: React.RefObject<boolean>;
		handleQuickFilterChange: (filterId: string) => Promise<void>;
		handleSort: (columnId: string) => Promise<void>;
		getRowId: (item: T) => string;
		isRowSelected: (item: T) => boolean;
		toggleRowSelection: (
			item: T,
			event?: React.MouseEvent<Element, MouseEvent> | undefined,
		) => void;
		toggleSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
		effectiveColumns: ColumnDef<T>[];
		query: FwQueryState<T>;
		columns: ColumnDef<T>[];
		quickFilters?: QuickFilterDef<T>[];
		filters?: FilterDef<T>[];
		onRowClick?: (item: T) => void;
		rowClassName?: (item: T) => string;
		emptyMessage?: string;
		title?: string;
		description?: string;
		showQuickFilters?: boolean;
		rowSelection?: RowSelectionOptions<T>;
		showPager?: boolean;
	}) {
		this.activeQuickFilter = props.activeQuickFilter;
		this.setActiveQuickFilter = props.setActiveQuickFilter;
		this.sortField = props.sortField;
		this.setSortField = props.setSortField;
		this.sortOrder = props.sortOrder;
		this.setSortOrder = props.setSortOrder;
		this.selectedRows = props.selectedRows;
		this.setSelectedRows = props.setSelectedRows;
		this.quickFilterSignatures = props.quickFilterSignatures;
		this.setQuickFilterSignatures = props.setQuickFilterSignatures;
		this.quickFilterActionInProgress = props.quickFilterActionInProgress;
		this.handleQuickFilterChange = props.handleQuickFilterChange;
		this.handleSort = props.handleSort;
		this.getRowId = props.getRowId;
		this.isRowSelected = props.isRowSelected;
		this.toggleRowSelection = props.toggleRowSelection;
		this.toggleSelectAll = props.toggleSelectAll;
		this.effectiveColumns = props.effectiveColumns;
		this.query = props.query;
		this.columns = props.columns;
		this.quickFilters = props.quickFilters;
		this.filters = props.filters;
		this.onRowClick = props.onRowClick;
		this.rowClassName = props.rowClassName;
		this.emptyMessage = props.emptyMessage;
		this.title = props.title;
		this.description = props.description;
		this.showQuickFilters = props.showQuickFilters;
		this.rowSelection = props.rowSelection;
		this.showPager = props.showPager;
	}
}

export interface UseDataTableProps<T> {
	query: FwQueryState<T>;
	columns: ColumnDef<T>[];
	quickFilters?: QuickFilterDef<T>[];
	filters?: FilterDef<T>[];
	onRowClick?: (item: T) => void;
	rowClassName?: (item: T) => string;
	emptyMessage?: string;
	title?: string;
	description?: string;
	showQuickFilters?: boolean;
	rowSelection?: RowSelectionOptions<T>;
	showPager?: boolean;
}
